import React, { useState, useEffect, useMemo } from 'react';
import {
  useReactTable,
  getCoreRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  getFilteredRowModel,
  ColumnDef,
  flexRender,
  SortingState,
  PaginationState,
} from '@tanstack/react-table';
import { 
  FiMoreVertical, 
  FiRotateCcw, 
  FiUserX, 
  FiUserCheck, 
  FiTrash2, 
  FiChevronLeft, 
  FiChevronRight, 
  FiChevronsLeft, 
  FiChevronsRight,
  FiSearch,
  FiAlertTriangle,
  FiShield,
  FiX
} from 'react-icons/fi';
import { authService } from '../services/authService';
import { User, UserRole } from '../types';
import LoadingSpinner from './LoadingSpinner';

interface UserManagementProps {
  onUserListUpdate?: () => void;
}

const ROLE_OPTIONS: { value: UserRole; label: string }[] = [
  { value: 'admin', label: 'Admin' },
  { value: 'hr_clerk', label: 'HR Clerk' },
  { value: 'manager', label: 'Manager' },
  { value: 'viewer', label: 'Viewer' },
  { value: 'candidate', label: 'Candidate' },
];

interface ActionMenuProps {
  user: User;
  onResetPassword: (user: User) => void;
  onToggleStatus: (user: User) => void;
  onChangeRole: (user: User) => void;
  onDeleteUser: (user: User) => void;
  isCurrentUser: boolean;
}

const ActionMenu: React.FC<ActionMenuProps> = ({
  user,
  onResetPassword,
  onToggleStatus,
  onChangeRole,
  onDeleteUser,
  isCurrentUser
}) => {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <div className="relative">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="p-2 rounded-full hover:bg-gray-100 transition-colors"
      >
        <FiMoreVertical className="h-4 w-4" />
      </button>
      
      {isOpen && (
        <>
          <div 
            className="fixed inset-0 z-10" 
            onClick={() => setIsOpen(false)}
          />
          <div className="absolute right-0 mt-2 w-48 bg-white rounded-md shadow-lg z-20 border border-gray-200">
            <div className="py-1">
              <button
                onClick={() => {
                  onResetPassword(user);
                  setIsOpen(false);
                }}
                className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-100 flex items-center gap-2"
              >
                <FiRotateCcw className="h-4 w-4" />
                Reset Password
              </button>
              
              {!isCurrentUser && (
                <button
                  onClick={() => {
                    onChangeRole(user);
                    setIsOpen(false);
                  }}
                  className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-100 flex items-center gap-2"
                >
                  <FiShield className="h-4 w-4" />
                  Change Role
                </button>
              )}

              {!isCurrentUser && (
                <button
                  onClick={() => {
                    onToggleStatus(user);
                    setIsOpen(false);
                  }}
                  className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-100 flex items-center gap-2"
                >
                  {user.isActive ? (
                    <>
                      <FiUserX className="h-4 w-4" />
                      Disable User
                    </>
                  ) : (
                    <>
                      <FiUserCheck className="h-4 w-4" />
                      Enable User
                    </>
                  )}
                </button>
              )}
              
              {!isCurrentUser && (
                <button
                  onClick={() => {
                    onDeleteUser(user);
                    setIsOpen(false);
                  }}
                  className="w-full px-4 py-2 text-left text-sm text-red-600 hover:bg-red-50 flex items-center gap-2"
                >
                  <FiTrash2 className="h-4 w-4" />
                  Delete User
                </button>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
};

interface ConfirmationModalProps {
  isOpen: boolean;
  title: string;
  message: string;
  confirmText: string;
  cancelText: string;
  onConfirm: () => void;
  onCancel: () => void;
  isDestructive?: boolean;
  isLoading?: boolean;
}

const ConfirmationModal: React.FC<ConfirmationModalProps> = ({
  isOpen,
  title,
  message,
  confirmText,
  cancelText,
  onConfirm,
  onCancel,
  isDestructive = false,
  isLoading = false,
}) => {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
        <div className="flex items-center gap-3 mb-4">
          <FiAlertTriangle className={`h-6 w-6 ${isDestructive ? 'text-red-500' : 'text-yellow-500'}`} />
          <h3 className="text-lg font-semibold text-gray-900">{title}</h3>
        </div>
        
        <p className="text-gray-600 mb-6">{message}</p>
        
        <div className="flex gap-3 justify-end">
          <button
            onClick={onCancel}
            className="px-4 py-2 text-gray-700 border border-gray-300 rounded-md hover:bg-gray-50 transition-colors"
          >
            {cancelText}
          </button>
          <button
            onClick={onConfirm}
            disabled={isLoading}
            className={`px-4 py-2 text-white rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${
              isDestructive 
                ? 'bg-red-600 hover:bg-red-700' 
                : 'bg-blue-600 hover:bg-blue-700'
            }`}
          >
            {isLoading ? (
              <div className="flex items-center gap-2">
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                Loading...
              </div>
            ) : (
              confirmText
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

interface PasswordResetModalProps {
  isOpen: boolean;
  user: User | null;
  message: string;
  onClose: () => void;
}

const PasswordResetModal: React.FC<PasswordResetModalProps> = ({
  isOpen,
  user,
  message,
  onClose,
}) => {
  if (!isOpen || !user) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-semibold text-gray-900">Password Reset Email Sent</h3>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
          >
            <FiX className="h-5 w-5" />
          </button>
        </div>
        
        <div className="mb-4">
          <p className="text-gray-600">
            {message}
          </p>
        </div>
        
        <div className="flex gap-3 justify-end">
          <button
            onClick={onClose}
            className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
          >
            Close
          </button>
        </div>
        
        <p className="text-xs text-gray-500 mt-3">
          The user will receive an email with a secure link to reset their password. The link will expire in 24 hours.
        </p>
      </div>
    </div>
  );
};

interface ChangeRoleModalProps {
  isOpen: boolean;
  user: User | null;
  isLoading: boolean;
  onConfirm: (role: UserRole) => void;
  onCancel: () => void;
}

const ChangeRoleModal: React.FC<ChangeRoleModalProps> = ({
  isOpen,
  user,
  isLoading,
  onConfirm,
  onCancel,
}) => {
  const [selectedRole, setSelectedRole] = useState<UserRole>('candidate');

  useEffect(() => {
    if (user) setSelectedRole(user.role);
  }, [user]);

  if (!isOpen || !user) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
        <div className="flex items-center gap-3 mb-4">
          <FiShield className="h-6 w-6 text-blue-500" />
          <h3 className="text-lg font-semibold text-gray-900">Change Role</h3>
        </div>

        <p className="text-gray-600 mb-4">
          Select a new role for <span className="font-medium">{user.email}</span>.
          The user will be signed out and must log in again for the change to take effect.
        </p>

        <select
          value={selectedRole}
          onChange={(e) => setSelectedRole(e.target.value as UserRole)}
          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500 mb-6"
        >
          {ROLE_OPTIONS.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>

        <div className="flex gap-3 justify-end">
          <button
            onClick={onCancel}
            className="px-4 py-2 text-gray-700 border border-gray-300 rounded-md hover:bg-gray-50 transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={() => onConfirm(selectedRole)}
            disabled={isLoading || selectedRole === user.role}
            className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isLoading ? (
              <div className="flex items-center gap-2">
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                Updating...
              </div>
            ) : (
              'Update Role'
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

const UserManagement: React.FC<UserManagementProps> = ({ onUserListUpdate }) => {
  const [data, setData] = useState<User[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [sorting, setSorting] = useState<SortingState>([]);
  const [searchTerm, setSearchTerm] = useState(''); // Input value
  const [activeSearchTerm, setActiveSearchTerm] = useState(''); // Applied search
  const [{ pageIndex, pageSize }, setPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: 10,
  });
  const [paginationTokens, setPaginationTokens] = useState<{[key: number]: string}>({});
  const [hasNextPage, setHasNextPage] = useState(false);

  // Modal states
  const [confirmModal, setConfirmModal] = useState<{
    isOpen: boolean;
    title: string;
    message: string;
    confirmText: string;
    onConfirm: () => void;
    isDestructive?: boolean;
  }>({
    isOpen: false,
    title: '',
    message: '',
    confirmText: '',
    onConfirm: () => {},
  });

  const [passwordResetModal, setPasswordResetModal] = useState<{
    isOpen: boolean;
    user: User | null;
    message: string;
  }>({
    isOpen: false,
    user: null,
    message: '',
  });

  const [isResettingPassword, setIsResettingPassword] = useState(false);

  const [roleModal, setRoleModal] = useState<{
    isOpen: boolean;
    user: User | null;
  }>({
    isOpen: false,
    user: null,
  });
  const [isUpdatingRole, setIsUpdatingRole] = useState(false);

  const pagination = useMemo(
    () => ({
      pageIndex,
      pageSize,
    }),
    [pageIndex, pageSize]
  );

  const fetchUsers = async (page: number = pageIndex) => {
    setIsLoading(true);
    setError(null);
    
    try {
      // Get token for current page (undefined for page 0)
      const token = page === 0 ? undefined : paginationTokens[page];
      
      const response = await authService.listUsers(pageSize, token, activeSearchTerm);
      
      if (response.success && response.data) {
        const responseData = response.data;
        setData(responseData.users);
        
        // Store next token for next page if it exists
        if (responseData.paginationToken) {
          setPaginationTokens(prev => ({
            ...prev,
            [page + 1]: responseData.paginationToken!
          }));
          setHasNextPage(true);
        } else {
          setHasNextPage(false);
        }
      } else {
        setError(response.error || 'Failed to fetch users');
      }
    } catch (err: any) {
      setError(err.message || 'An unexpected error occurred');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSearch = () => {
    setActiveSearchTerm(searchTerm);
    setPagination(prev => ({ ...prev, pageIndex: 0 }));
    setPaginationTokens({});
  };

  const handleClearSearch = () => {
    setSearchTerm('');
    setActiveSearchTerm('');
    setPagination(prev => ({ ...prev, pageIndex: 0 }));
    setPaginationTokens({});
  };

  // Fetch when page, page size, or active search changes
  useEffect(() => {
    fetchUsers(pageIndex);
  }, [pageIndex, pageSize, activeSearchTerm]);

  const handleResetPassword = (user: User) => {
    setConfirmModal({
      isOpen: true,
      title: 'Reset Password',
      message: `Are you sure you want to reset the password for ${user.email}? A password reset email will be sent to the user.`,
      confirmText: 'Send Reset Email',
      onConfirm: async () => {
        setIsResettingPassword(true);
        try {
          const response = await authService.resetPassword(user.email);
          if (response.success) {
            setPasswordResetModal({
              isOpen: true,
              user,
              message: `Password reset email sent successfully to ${user.email}. The user will receive a secure link to reset their password.`,
            });
            onUserListUpdate?.();
          } else {
            setError(response.error || 'Failed to send password reset email');
          }
        } catch (err: any) {
          setError(err.message || 'Failed to send password reset email');
        } finally {
          setIsResettingPassword(false);
          setConfirmModal(prev => ({ ...prev, isOpen: false }));
        }
      },
    });
  };

  const handleToggleStatus = (user: User) => {
    const action = user.isActive ? 'disable' : 'enable';
    setConfirmModal({
      isOpen: true,
      title: `${action.charAt(0).toUpperCase() + action.slice(1)} User`,
      message: `Are you sure you want to ${action} ${user.email}?`,
      confirmText: `${action.charAt(0).toUpperCase() + action.slice(1)} User`,
      onConfirm: async () => {
        try {
          const response = await authService.updateUserStatus(user.email, !user.isActive);
          if (response.success) {
            await fetchUsers();
            onUserListUpdate?.();
          } else {
            setError(response.error || `Failed to ${action} user`);
          }
        } catch (err: any) {
          setError(err.message || `Failed to ${action} user`);
        }
        setConfirmModal(prev => ({ ...prev, isOpen: false }));
      },
      isDestructive: user.isActive,
    });
  };

  const handleChangeRole = (user: User) => {
    setRoleModal({ isOpen: true, user });
  };

  const handleConfirmRoleChange = async (role: UserRole) => {
    if (!roleModal.user) return;
    setIsUpdatingRole(true);
    try {
      const response = await authService.updateUserRole(roleModal.user.email, role);
      if (response.success) {
        await fetchUsers();
        onUserListUpdate?.();
        setRoleModal({ isOpen: false, user: null });
      } else {
        setError(response.error || 'Failed to update user role');
        setRoleModal({ isOpen: false, user: null });
      }
    } catch (err: any) {
      setError(err.message || 'Failed to update user role');
      setRoleModal({ isOpen: false, user: null });
    } finally {
      setIsUpdatingRole(false);
    }
  };

  const handleDeleteUser = (user: User) => {
    setConfirmModal({
      isOpen: true,
      title: 'Delete User',
      message: `Are you sure you want to permanently delete ${user.email}? This action cannot be undone.`,
      confirmText: 'Delete User',
      onConfirm: async () => {
        try {
          const response = await authService.deleteUser(user.email);
          if (response.success) {
            await fetchUsers();
            onUserListUpdate?.();
          } else {
            setError(response.error || 'Failed to delete user');
          }
        } catch (err: any) {
          setError(err.message || 'Failed to delete user');
        }
        setConfirmModal(prev => ({ ...prev, isOpen: false }));
      },
      isDestructive: true,
    });
  };

  const getCurrentUserEmail = () => {
    const user = authService.getStoredUser();
    return user?.email;
  };

  const columns = useMemo<ColumnDef<User>[]>(
    () => [
      {
        accessorKey: 'firstName',
        header: 'Name',
        cell: ({ row }) => (
          <div>
            <div className="font-medium text-gray-900">
              {row.original.firstName} {row.original.lastName}
            </div>
            <div className="text-sm text-gray-500">{row.original.email}</div>
          </div>
        ),
      },
      {
        accessorKey: 'role',
        header: 'Role',
        cell: ({ getValue }) => {
          const role = getValue() as string;
          const roleColors = {
            admin: 'bg-accent-100 text-accent-800 border border-accent-200',
            hr_clerk: 'bg-brand-100 text-brand-800 border border-brand-200',
            manager: 'bg-success-100 text-success-800 border border-success-200',
            candidate: 'bg-primary-100 text-primary-800 border border-primary-200',
            viewer: 'bg-gray-100 text-gray-800 border border-gray-200',
          };
          
          return (
            <span className={`px-3 py-1.5 text-xs font-semibold rounded-full ${roleColors[role as keyof typeof roleColors] || roleColors.candidate}`}>
              {role.replace('_', ' ').toUpperCase()}
            </span>
          );
        },
      },
      {
        accessorKey: 'isActive',
        header: 'Status',
        cell: ({ getValue }) => {
          const isActive = getValue() as boolean;
          return (
            <span className={`px-3 py-1.5 text-xs font-semibold rounded-full ${
              isActive 
                ? 'bg-success-100 text-success-800 border border-success-200' 
                : 'bg-error-100 text-error-800 border border-error-200'
            }`}>
              {isActive ? 'Active' : 'Disabled'}
            </span>
          );
        },
      },
      {
        accessorKey: 'createdAt',
        header: 'Created',
        cell: ({ getValue }) => {
          const date = getValue() as string;
          return date ? new Date(date).toLocaleDateString() : '';
        },
      },
      {
        id: 'actions',
        header: '',
        cell: ({ row }) => {
          const currentUserEmail = getCurrentUserEmail();
          const isCurrentUser = row.original.email === currentUserEmail;
          
          return (
            <ActionMenu
              user={row.original}
              onResetPassword={handleResetPassword}
              onToggleStatus={handleToggleStatus}
              onChangeRole={handleChangeRole}
              onDeleteUser={handleDeleteUser}
              isCurrentUser={isCurrentUser}
            />
          );
        },
      },
    ],
    []
  );

  const table = useReactTable({
    data,
    columns,
    pageCount: -1, // Server-side pagination with unknown total
    manualPagination: true,
    state: {
      sorting,
      pagination,
    },
    onSortingChange: setSorting,
    onPaginationChange: setPagination,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
  });

  if (isLoading) {
    return (
      <div className="flex justify-center items-center py-8">
        <LoadingSpinner />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-md p-4">
          <div className="flex">
            <FiAlertTriangle className="h-5 w-5 text-red-400" />
            <div className="ml-3">
              <h3 className="text-sm font-medium text-red-800">Error</h3>
              <div className="mt-2 text-sm text-red-700">{error}</div>
            </div>
          </div>
        </div>
      )}

      {/* Search */}
      <div className="flex items-center gap-4">
        <div className="relative flex-1 max-w-sm">
          <FiSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
          <input
            type="text"
            placeholder="Search users (email)..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
            className="pl-10 pr-10 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500 w-full"
          />
          {searchTerm && (
            <button
              onClick={handleClearSearch}
              className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
            >
              <FiX className="h-4 w-4" />
            </button>
          )}
        </div>
        <button
          onClick={handleSearch}
          className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors flex items-center gap-2"
        >
          <FiSearch className="h-4 w-4" />
          Search
        </button>
      </div>

      {/* Table */}
      <div className="bg-white rounded-lg shadow overflow-visible">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            {table.getHeaderGroups().map((headerGroup) => (
              <tr key={headerGroup.id}>
                {headerGroup.headers.map((header) => (
                  <th
                    key={header.id}
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                    onClick={header.column.getToggleSortingHandler()}
                  >
                    {header.isPlaceholder
                      ? null
                      : flexRender(
                          header.column.columnDef.header,
                          header.getContext()
                        )}
                    {{
                      asc: ' ↑',
                      desc: ' ↓',
                    }[header.column.getIsSorted() as string] ?? null}
                  </th>
                ))}
              </tr>
            ))}
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {table.getRowModel().rows.map((row) => (
              <tr key={row.id} className="hover:bg-gray-50">
                {row.getVisibleCells().map((cell) => (
                  <td key={cell.id} className="px-6 py-4 whitespace-nowrap">
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <span className="text-sm text-gray-700">
            Page {table.getState().pagination.pageIndex + 1}
          </span>
        </div>
        
        <div className="flex items-center gap-2">
          <button
            onClick={() => table.setPageIndex(0)}
            disabled={!table.getCanPreviousPage()}
            className="p-2 rounded-md border border-gray-300 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
          >
            <FiChevronsLeft className="h-4 w-4" />
          </button>
          <button
            onClick={() => table.previousPage()}
            disabled={!table.getCanPreviousPage()}
            className="p-2 rounded-md border border-gray-300 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
          >
            <FiChevronLeft className="h-4 w-4" />
          </button>
          
          <span className="px-3 py-2 text-sm">
            Page {table.getState().pagination.pageIndex + 1}
          </span>
          
          <button
            onClick={() => table.nextPage()}
            disabled={!hasNextPage}
            className="p-2 rounded-md border border-gray-300 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
          >
            <FiChevronRight className="h-4 w-4" />
          </button>
        </div>
      </div>

      {/* Modals */}
      <ConfirmationModal
        isOpen={confirmModal.isOpen}
        title={confirmModal.title}
        message={confirmModal.message}
        confirmText={confirmModal.confirmText}
        cancelText="Cancel"
        onConfirm={confirmModal.onConfirm}
        onCancel={() => setConfirmModal(prev => ({ ...prev, isOpen: false }))}
        isDestructive={confirmModal.isDestructive}
        isLoading={isResettingPassword}
      />

      <PasswordResetModal
        isOpen={passwordResetModal.isOpen}
        user={passwordResetModal.user}
        message={passwordResetModal.message}
        onClose={() => setPasswordResetModal({ isOpen: false, user: null, message: '' })}
      />

      <ChangeRoleModal
        isOpen={roleModal.isOpen}
        user={roleModal.user}
        isLoading={isUpdatingRole}
        onConfirm={handleConfirmRoleChange}
        onCancel={() => setRoleModal({ isOpen: false, user: null })}
      />
    </div>
  );
};

export default UserManagement;
